import { t } from '../../strings';
import { listOf, readableDay } from '../../strings/format';
import type { View, ViewContext } from '../router';
import type { Species } from '../../types';
import { el, ensureStyle } from '../dom';
import { activePacks, newestBatch, newestBatchDays, packIdOf, speciesList } from '../../core/region';
import {
  FILTER_STANDINGS,
  matchesStanding,
  MISSED_DAYS,
  recentMisses,
  speciesStandings,
  standingTally,
  type FilterStanding,
  type SpeciesStanding,
} from '../../core/progress';
import { studyList } from '../../core/ignored';
import { openModeMenu } from '../components/mode-menu';
import { focusCodes, type Focus, type FocusKind } from '../../core/quiz/focus';
import { isIgnored, toggleIgnored } from '../../core/ignored';
import { speciesPhoto } from '../components/species-photo';
import { masteryDot, masteryDots } from '../components/mastery-dots';
import { icon } from '../components/icons';

// The whole region, in taxonomic order, grouped by family.
//
// This was a flat eager list, which was right at 22 species and wrong at 120:
// every row asked for its thumbnail on render, so opening the Library fetched
// and decoded the entire region's images to show the ten rows that fit on a
// phone. Three changes carry it: thumbnails load on approach, families give the
// scroll landmarks, and a name filter means finding one bird doesn't require
// scrolling past a hundred others.
//
// It is also where the card set finally becomes visible per bird. Every other
// screen aggregates it — three rings, a fortnight of bars, a due forecast — so
// "how am I doing on THIS one" had no answer anywhere. Each row carries three
// dots, one per skill, and the chips beside the name filter cut the list to the
// birds with something outstanding.

ensureStyle(
  'library-css',
  `
  .library { padding: 18px var(--pad) 8px; }
  .library h1 { font-size:30px; margin:0 0 5px; }
  .library > .tally { margin-bottom:14px; display:block; }
  .libtools { display:flex; flex-direction:column; gap:9px; margin-bottom:6px; }
  .libfilter { position:relative; }
  .libfilter input { padding-right:44px; }
  .libfilter button { position:absolute; right:4px; top:50%; transform:translateY(-50%);
    color:var(--ink-soft); }
  /* Scrolls rather than wraps: three chips plus their counts overflow a narrow
     phone, and a second row of chips would push the list itself off the screen
     on exactly the devices with the least of it. */
  .libchips { display:flex; gap:7px; overflow-x:auto; padding-bottom:2px;
    scrollbar-width:none; }
  .libchips::-webkit-scrollbar { display:none; }
  .libchips .chip { flex:none; padding:5px 12px; font-size:13px; gap:7px;
    cursor:pointer; }
  .libchips .chip .n { font-family:var(--font-mono); font-size:11px;
    color:var(--ink-soft); }
  .libchips .chip[aria-pressed='true'] { background:var(--accent);
    border-color:var(--accent); color:var(--on-accent); }
  .libchips .chip[aria-pressed='true'] .n { color:var(--on-accent); opacity:0.7; }
  /* The halo on a due dot is a gap punched in the ring, so it has to be told
     what it is sitting on once the chip fills in. */
  .libchips .chip[aria-pressed='true'] .mdot { --dot-gap:var(--accent); }
  .libchips .chip:disabled { opacity:0.45; cursor:default; }
  /* Appears only once the list has actually been narrowed, which is what makes
     the ~44px it costs acceptable: it arrives at the moment it is the likeliest
     next thing, directly under the control that just changed the list. Under
     the chips rather than in a sticky footer, because the nav bar already owns
     the bottom of the screen. */
  .libstudy { width:100%; justify-content:center; gap:8px; margin-top:2px; }
  .splist { display:flex; flex-direction:column; }
  /* Sticky so the family you are looking at stays named while you scroll it.
     Opaque background for the same reason the nav bar has one — rows pass
     underneath. */
  .famhead { position:sticky; top:0; z-index:1; display:flex; align-items:baseline;
    gap:8px; padding:9px 4px 6px; background:var(--paper);
    border-bottom:1.5px solid var(--hairline-strong); }
  .famhead .fam { flex:1; font-family:var(--font-display); font-style:italic;
    font-size:14px; color:var(--ink); }
  /* Quiet like the set-aside button on a row, and for the same reason — this
     one is on fifty-one headings. */
  .famhead .famstudy { padding:4px 2px 4px 8px; color:var(--ink-soft); opacity:.4; }
  .famhead .famstudy:hover, .famhead .famstudy:focus-visible { opacity:1;
    color:var(--accent); }
  /* The row is a link with a button beside it, not a link containing one:
     interactive content inside an <a> is invalid HTML, and browsers that do
     render it hand the click to the anchor anyway. */
  .sprow { display:flex; align-items:center; gap:4px; padding:0 4px;
    border-bottom:1.5px solid var(--hairline); }
  .sprow .spmain { flex:1; min-width:0; display:flex; align-items:center; gap:13px;
    padding:11px 0; text-decoration:none; color:var(--ink); }
  .famgroup:last-child .sprow:last-child { border-bottom:none; }
  /* Quiet until wanted: 160 rows each shouting a second control would make the
     list about the button rather than about the birds. It keeps a full tap
     target while showing a light glyph. */
  .sprow .setaside { flex:none; padding:9px 6px; color:var(--ink-soft); opacity:.4; }
  .sprow .setaside:hover, .sprow .setaside:focus-visible { opacity:1; }
  /* A set-aside bird is only ever on screen inside its own lens, so it can
     afford to look struck out rather than merely dimmed. */
  .sprow.aside .spmain { opacity:.55; }
  .sprow.aside .common { text-decoration:line-through; text-decoration-thickness:1.5px; }
  .sprow.aside .setaside { opacity:1; color:var(--accent); }
  /* The thumbnails get the hand-cut edge too — a strip of pressed specimens. */
  .sprow .spphoto { flex:none; width:54px; height:54px; border-radius:var(--radius-hand);
    border:1.5px solid var(--hairline); }
  .sprow:nth-child(even) .spphoto { border-radius:var(--radius-hand-alt); }
  .sprow .names { flex:1; min-width:0; }
  .sprow .common { font-family:var(--font-display); font-size:17.5px; display:block;
    line-height:1.2; }
  .sprow .sci { font-style:italic; font-size:13px; color:var(--ink-soft); display:block; }
  /* Only rendered when more than one pack is selected, where the list is mixed
     and "which of these is a bird I'd see here" stops being obvious. */
  .sprow .pack { display:block; margin-top:2px; }
  /* Right-hand column: what the bird has, above how you're doing on it. */
  .sprow .state { flex:none; display:flex; flex-direction:column; align-items:flex-end;
    gap:5px; }
  .sprow .counts { display:flex; align-items:center; justify-content:flex-end;
    gap:4px; font-size:12px; color:var(--ink-soft); min-height:15px; }
  .library .nomatch { padding:26px 4px; text-align:center; }
  /* [hidden] is a UA rule, and any author display: beats it — which is every
     element the filter hides. */
  .library [hidden] { display:none; }
`,
);

/**
 * Where the Library keeps its place while the learner looks at one bird.
 *
 * sessionStorage, not the progress store: this is a fact about a visit rather
 * than about the learner, so it has no business syncing to an account or
 * outliving the tab. Filter and scroll travel together because they are one
 * thing — "where I was in the list" is meaningless with the filter dropped.
 */
const PLACE_KEY = 'syrinx.library.place';

/**
 * The one chip that can be down.
 *
 * "Set aside" joins the standings as another lens rather than as a separate
 * switch because it answers the same kind of question — show me a subset of the
 * region — and because the two would otherwise contradict each other on screen:
 * "Unmet" and "Set aside" both pressed describes birds that are, by
 * construction, never going to be asked about.
 *
 * "Missed" and "Just added" arrived with focused rounds, and both sit after the
 * three standings rather than among them, because neither is one. Missed reads
 * the review log instead of the card state, and earns its place beside Weak's
 * group because they answer different questions: Weak is the scheduler's
 * opinion of a memory right now, Missed is a record of something that actually
 * went wrong this week. "Just added" is not about the learner at all — it is
 * the newest birds in the pack, which the schedule otherwise reaches last of
 * everything there is (see `newestBatch`).
 */
type Lens = FilterStanding | 'missed' | 'batch' | 'ignored';

const LENSES: readonly Lens[] = [...FILTER_STANDINGS, 'missed', 'batch', 'ignored'] as const;

/**
 * The lenses a round can be built from, and what the focus token calls them.
 *
 * "Set aside" is absent on purpose: those birds are by definition never asked
 * about, so there is no round to offer under that chip.
 */
const LENS_FOCUS: Partial<Record<Lens, FocusKind>> = {
  due: 'due',
  weak: 'weak',
  missed: 'missed',
  unmet: 'unmet',
  batch: 'batch',
};

interface Place {
  filter: string;
  scroll: number;
  /** The chip that was down, if any. Absent in anything an older build wrote. */
  lens?: Lens | null;
  /**
   * Whether the visit ended by starting a round from this list.
   *
   * A focused round can now be launched from the home screen too, so arriving
   * back here from `/quiz/` no longer means the round came from this list —
   * Home, a round, then the Library tab is the same arrival and should open a
   * fresh look. This is the only thing that tells the two apart.
   */
  viaStudy?: boolean;
}

function readPlace(): Place | null {
  try {
    const raw = sessionStorage.getItem(PLACE_KEY);
    const place = raw ? (JSON.parse(raw) as Place) : null;
    if (!place || typeof place.filter !== 'string') return null;
    // Whatever is in storage is whatever some build wrote — a chip this one has
    // never heard of would filter the list to nothing with no way to see why.
    if (place.lens && !LENSES.includes(place.lens)) place.lens = null;
    return place;
  } catch {
    // Unavailable (private mode) or malformed — the list opens at the top,
    // which is exactly what it did before it remembered anything.
    return null;
  }
}

function writePlace(place: Place): void {
  try {
    sessionStorage.setItem(PLACE_KEY, JSON.stringify(place));
  } catch {
    /* nothing here is worth failing a navigation over */
  }
}

export const libraryView: View = async (root: HTMLElement, ctx: ViewContext) => {
  const species = speciesList();
  // One pass over the card set for the whole list. The store is already in
  // memory by the time any view runs, so this is a microtask in practice — and
  // awaiting it here rather than filling the dots in afterwards is what lets the
  // chips filter on arrival instead of half a frame later.
  const now = new Date();
  // Both of these ride along in the same await for the same reason the
  // standings do: the chips have to be able to filter the moment the list is on
  // screen. Neither can go stale while the Library is open — no question is
  // answered here, and no pack arrives — so `countLenses` below stays
  // synchronous, which is what lets it re-run on every set-aside.
  const [standings, missed] = await Promise.all([
    speciesStandings(species.map((sp) => sp.code), now),
    recentMisses(MISSED_DAYS, now),
  ]);
  // Read off the study list rather than the region, so a batch of twenty with
  // three of them set aside is a chip that says seventeen.
  const batch = new Set(newestBatch(studyList()).map((sp) => sp.code));
  const batchDays = [...newestBatchDays(studyList()).values()].sort();

  /**
   * What each chip would show, right now.
   *
   * Recomputed on every set-aside rather than captured once: a bird taken out
   * of the rounds leaves the three standing counts too, and a chip reading "12
   * due" over a list of eleven is the sort of small lie that makes a learner
   * stop believing the numbers. Still NOT recomputed as the name filter is
   * typed — see standingTally.
   */
  const countLenses = (): Record<Lens, number> => {
    const studied: SpeciesStanding[] = [];
    let aside = 0;
    let missedCount = 0;
    let batchCount = 0;
    for (const sp of species) {
      if (isIgnored(sp.code)) {
        aside += 1;
        continue;
      }
      studied.push(standings.get(sp.code)!);
      if (missed.has(sp.code)) missedCount += 1;
      if (batch.has(sp.code)) batchCount += 1;
    }
    return {
      ...standingTally(studied),
      missed: missedCount,
      batch: batchCount,
      ignored: aside,
    };
  };
  let counts = countLenses();
  // A species page was the one exit that came back; a round started from this
  // list is now the second, and coming back to an unfiltered Library would
  // throw away the very narrowing that chose the round's birds. A round started
  // from the HOME screen is not that — it arrives from `/quiz/` all the same,
  // so the saved place says which it was. Reaching the Library from the tab bar
  // or a cold launch still means a fresh look at the region.
  const saved = readPlace();
  const place = ctx.from.startsWith('/species/')
    ? saved
    : ctx.from.startsWith('/quiz/') && saved?.viaStudy
      ? saved
      : null;
  const list = el('div.splist', {});
  const groups = groupByFamily(species);

  // Every row is built once, up front; filtering only toggles visibility. The
  // rows are cheap — it was the images that weren't, and those are lazy now.
  const rows: {
    sp: Species;
    node: HTMLElement;
    haystack: string;
    standing: SpeciesStanding;
  }[] = [];
  const groupNodes: {
    node: HTMLElement;
    count: HTMLElement;
    rows: HTMLElement[];
    /** Parallel to `rows`, so a hidden row and its species stay lined up. */
    members: Species[];
    family: string;
    study: HTMLButtonElement;
    /** What its button would start, rewritten by every `apply`. */
    studyFocus: Focus;
  }[] = [];

  for (const [family, members] of groups) {
    const count = el('span.n.label', {}, `${members.length}`);
    // Quiet until wanted, exactly like the set-aside button on a row, and for
    // the same reason: fifty-one headings each shouting a control would make
    // the list about the buttons.
    //
    // This is the only route that actually reaches "all the sparrows". Typing
    // it into the filter matches Song Sparrow and House Sparrow together, which
    // are Passerellidae and Passeridae — two different families, and precisely
    // the confusion a family round exists to fix.
    const famStudy = el('button.famstudy.btn.btn-plain', {
      type: 'button',
      title: t('library.study.family', { family }),
    }) as HTMLButtonElement;
    famStudy.append(icon('binoculars', { size: 15 }));
    const group = el(
      'div.famgroup',
      {},
      el('div.famhead', {}, el('span.fam', {}, family), count, famStudy),
    );
    const nodes: HTMLElement[] = [];
    for (const sp of members) {
      const standing = standings.get(sp.code)!;
      const node = speciesRow(sp, standing, now, ctx, () => apply());
      group.append(node);
      nodes.push(node);
      // The pack name is searchable whether or not it is shown, so "cape town"
      // filters the list down to that pack even from a single-pack selection.
      rows.push({
        sp,
        node,
        standing,
        haystack: `${sp.common} ${sp.sci} ${sp.family ?? ''} ${packLabel(sp.code)}`.toLowerCase(),
      });
    }
    list.append(group);
    const entry = {
      node: group,
      count,
      rows: nodes,
      members,
      family,
      study: famStudy,
      studyFocus: { kind: 'family', family } as Focus,
    };
    famStudy.addEventListener('click', () =>
      openModeMenu(famStudy, {
        focus: entry.studyFocus,
        codes: focusCodes(entry.studyFocus),
        navigate: (path) => {
          viaStudy = true;
          ctx.navigate(path);
        },
      }),
    );
    groupNodes.push(entry);
  }

  const tally = el(
    'span.tally.label',
    {},
    t('library.tally.whole', { count: species.length, ...media(species) }),
  );
  const nomatch = el('div.nomatch.meta', {}, '');
  nomatch.hidden = true;

  const clear = el(
    'button.btn.btn-plain',
    { type: 'button', 'aria-label': t('library.filter.clear') },
    icon('cross', { size: 18 }),
  ) as HTMLButtonElement;
  clear.hidden = true;

  const input = el('input.input', {
    type: 'search',
    placeholder: t('library.filter.placeholder'),
    autocomplete: 'off',
    'aria-label': t('library.filter.label'),
    value: place?.filter ?? '',
  }) as HTMLInputElement;

  // A chip with nothing behind it can't be pressed, so a restored one that has
  // since emptied — you studied everything that was due — comes back up rather
  // than reopening the Library on a blank list.
  let lens: Lens | null = place?.lens && counts[place.lens] ? place.lens : null;

  const chips = LENSES.map((which) => {
    const n = el('span.n', {}, String(counts[which]));
    const chip = el(
      'button.chip',
      {
        type: 'button',
        'aria-pressed': 'false',
        title: which === 'batch' ? batchHelp(batchDays) : CHIP_HELP[which],
      },
      chipGlyph(which),
      CHIP_LABEL[which],
      n,
    ) as HTMLButtonElement;
    chip.addEventListener('click', () => {
      // A second press releases it: a chip is a lens, not a mode to escape from.
      lens = lens === which ? null : which;
      apply();
    });
    return { which, chip, n };
  });

  // What the study button would start, rewritten by every `apply` so that the
  // button and the count above it can never disagree.
  let focus: Focus = { kind: 'codes', codes: [] };
  // Set by either study button, read by the next visit — see `Place.viaStudy`.
  let viaStudy = false;
  const study = el('button.libstudy.btn.btn-primary', { type: 'button' }) as HTMLButtonElement;
  study.hidden = true;
  study.addEventListener('click', () => {
    // The button is hidden whenever there is nothing to offer, and `focus` is
    // emptied alongside it — so this guard only fires for a press that reached a
    // button no learner could see. Cheap, and it is the difference between doing
    // nothing and starting a round from whatever the last filter happened to be.
    const codes = focusCodes(focus);
    if (!codes.length) return;
    openModeMenu(study, {
      focus,
      codes,
      navigate: (path) => {
        viaStudy = true;
        ctx.navigate(path);
      },
    });
  });

  /** Whether a bird belongs under the chip that is down. */
  const inLens = (r: { sp: Species; standing: SpeciesStanding }): boolean => {
    if (lens === null || lens === 'ignored') return true;
    if (lens === 'missed') return missed.has(r.sp.code);
    if (lens === 'batch') return batch.has(r.sp.code);
    return matchesStanding(r.standing, lens);
  };

  const apply = () => {
    // Every word has to match somewhere, so "song sparrow" and "sparrow song"
    // both find the same bird.
    const terms = input.value.toLowerCase().split(/\s+/).filter(Boolean);
    clear.hidden = !terms.length;
    counts = countLenses();
    // Putting the last set-aside bird back takes its own lens away with it,
    // which would otherwise leave the Library holding an empty list open.
    if (lens === 'ignored' && counts.ignored === 0) lens = null;
    for (const c of chips) {
      c.chip.setAttribute('aria-pressed', String(c.which === lens));
      c.n.textContent = String(counts[c.which]);
      c.chip.disabled = counts[c.which] === 0 && c.which !== lens;
      // Nothing set aside means no chip at all rather than a dead one: this is
      // the only chip that is empty on a fresh install and stays that way for
      // most learners, and three chips is already a scrolling row on a phone.
      if (c.which === 'ignored') c.chip.hidden = counts.ignored === 0 && lens !== 'ignored';
    }
    let shown = 0;
    const visible: Species[] = [];
    for (const r of rows) {
      // Set-aside birds are held back from every other view of the list — the
      // point of setting one aside is not to be shown it — so their own lens is
      // the one place they appear, and it shows nothing else.
      const aside = isIgnored(r.sp.code);
      // The filters are an AND: the chip narrows the region to the birds with
      // work outstanding, the words find one of them.
      const hit =
        (lens === 'ignored' ? aside : !aside) &&
        terms.every((t) => r.haystack.includes(t)) &&
        inLens(r);
      r.node.hidden = !hit;
      if (hit) {
        shown++;
        visible.push(r.sp);
      }
    }
    for (const g of groupNodes) {
      const n = g.rows.filter((r) => !r.hidden).length;
      g.node.hidden = n === 0;
      g.count.textContent = String(n);
    }
    // "Study these 9" — the button the whole feature hangs off. It appears only
    // once the list has actually been narrowed: a focus of the whole region is
    // an ordinary round, and there are already five buttons for that. It never
    // appears under the Set aside lens either, since offering to quiz birds the
    // learner has just put away contradicts the chip above it.
    const focusable = shown > 0 && lens !== 'ignored' && (terms.length > 0 || lens !== null);
    study.hidden = !focusable;
    focus = { kind: 'codes', codes: [] };
    if (focusable) {
      study.replaceChildren(
        icon('binoculars', { size: 17 }),
        t('library.study.button', { n: shown }),
      );
      // The whole list narrowed to one family, with nothing typed, is that
      // family — and saying so keeps the round's own label meaningful.
      focus = wholeFamily(visible, terms)
        ? { kind: 'family', family: visible[0].family! }
        : { kind: (lens && LENS_FOCUS[lens]) ?? 'codes', codes: visible.map((sp) => sp.code) };
    }
    // Each family heading offers its own round, of exactly the members it is
    // currently counting — the group's count is rewritten above, and a button
    // beside a number that means something else would be a lie.
    for (const g of groupNodes) {
      const members = g.rows
        .map((node, i) => (node.hidden ? null : g.members[i]))
        .filter((sp): sp is Species => sp !== null);
      g.study.hidden = lens === 'ignored' || members.length === 0;
      g.studyFocus =
        members.length === g.members.length
          ? { kind: 'family', family: g.family }
          : { kind: 'codes', codes: members.map((sp) => sp.code) };
      g.study.setAttribute('aria-label', t('library.study.family', { family: g.family }));
    }
    // What "of" counts: the birds this lens could show at all, which is the
    // region minus the ones set aside — or, inside that lens, only those.
    const universe = lens === 'ignored' ? counts.ignored : rows.length - counts.ignored;
    const filtered = terms.length > 0 || lens !== null;
    tally.textContent = filtered
      ? shown
        ? t('library.tally.filtered', { shown, universe, ...media(visible) })
        : t('library.tally.filteredNone', { shown, universe })
      : counts.ignored
        ? t('library.tally.wholeAside', {
            count: universe,
            ...media(visible),
            aside: counts.ignored,
          })
        : t('library.tally.whole', { count: universe, ...media(visible) });
    nomatch.hidden = shown > 0;
    nomatch.textContent = shown ? '' : emptyMessage(input.value.trim(), lens);
  };

  input.addEventListener('input', apply);
  clear.addEventListener('click', () => {
    input.value = '';
    apply();
    input.focus();
  });

  root.append(
    el(
      'div.library',
      {},
      el('h1', {}, t('common.nav.library')),
      tally,
      el(
        'div.libtools',
        {},
        el('div.libfilter', {}, input, clear),
        el(
          'div.libchips',
          // Not "by how you are doing" any more: two of these chips are about
          // the pack and about what happened, not about the learner's memory.
          { role: 'group', 'aria-label': t('library.chips.label') },
          ...chips.map((c) => c.chip),
        ),
        study,
      ),
      nomatch,
      list,
    ),
  );

  // Always, not only when a filter was restored: the default view is itself a
  // filtered one now — the birds set aside are held back from it — and the chip
  // row needs a pass to hide the one chip that is empty on most installs.
  //
  // A restored filter also has to be applied before the scroll: with 140 species
  // hidden down to nine, the offset that was right in the filtered list is off
  // the end of the page in the full one.
  apply();
  if (place?.scroll) requestAnimationFrame(() => window.scrollTo(0, place.scroll));

  // The router tears the view down before it clears the outlet and scrolls the
  // page to the top, so this is the last moment any of it is still true.
  return () => writePlace({ filter: input.value, scroll: window.scrollY, lens, viaStudy });
};

/** What each chip is called, and the sentence behind it. */
const CHIP_LABEL: Record<Lens, string> = {
  due: t('library.chip.due'),
  weak: t('library.chip.weak'),
  unmet: t('library.chip.unmet'),
  missed: t('library.chip.missed'),
  batch: t('library.chip.batch'),
  ignored: t('library.chip.ignored'),
};

const CHIP_HELP: Record<Lens, string> = {
  due: t('library.chipHelp.due'),
  weak: t('library.chipHelp.weak'),
  unmet: t('library.chipHelp.unmet'),
  missed: t('library.chipHelp.missed'),
  batch: t('library.chipHelp.batch'),
  ignored: t('library.chipHelp.ignored'),
};

/**
 * "Just added" on its own means whenever; the day it happened is the fact worth
 * having. Two packs can have been added to on different days, and both batches
 * are offered, so both dates are named.
 */
function batchHelp(days: string[]): string {
  if (!days.length) return CHIP_HELP.batch;
  return t('library.chipHelp.batchOn', { when: listOf(days.map(readableDay)) });
}

/**
 * The glyph a chip opens with.
 *
 * The three standings borrow a mastery dot, so the chip and the dots on the
 * rows beneath it are visibly the same four states. The other three are not
 * standings and must not wear one — a dot on "Just added" would claim a state
 * of knowledge about birds the learner has not been asked about at all.
 */
function chipGlyph(which: Lens): HTMLElement {
  if (which === 'ignored') return icon('set-aside', { size: 13 });
  if (which === 'missed') return icon('cross', { size: 13 });
  if (which === 'batch') return icon('feather', { size: 13 });
  return masteryDot(which);
}

/**
 * Whether what is on screen is one whole family and nothing else.
 *
 * Worth checking so that filtering down to the sparrows by any route — the
 * heading's own button, or typing the family name — starts a round that calls
 * itself Passerellidae rather than "9 birds from the Library".
 */
function wholeFamily(visible: Species[], terms: string[]): boolean {
  if (!visible.length || !terms.length) return false;
  const family = visible[0].family;
  if (!family) return false;
  if (visible.some((sp) => sp.family !== family)) return false;
  return speciesList().filter((sp) => sp.family === family).length === visible.length;
}

/** Why the list is empty, in the terms of whichever filter emptied it. */
function emptyMessage(term: string, lens: Lens | null): string {
  if (!lens) return t('library.empty.term', { term });
  if (term) return t(`library.empty.termIn.${lens}`, { term });
  return t(`library.empty.lens.${lens}`);
}

/** The label of the pack a species is taught from, '' if there is only one. */
function packLabel(code: string): string {
  const packs = activePacks();
  if (packs.length < 2) return '';
  const id = packIdOf(code);
  return packs.find((m) => m.id === id)?.label ?? '';
}

function speciesRow(
  sp: Species,
  standing: SpeciesStanding,
  now: Date,
  ctx: ViewContext,
  onSetAside: () => void,
): HTMLElement {
  const photo = speciesPhoto(sp.photos[0], { thumb: true, lazy: true, alt: sp.common });
  const link = el(
    'a.spmain',
    { href: `#/species/${sp.code}` },
    photo.element,
    el(
      'span.names',
      {},
      el('span.common', {}, sp.common),
      el('span.sci', {}, sp.sci),
      packLabel(sp.code) && el('span.pack.label', {}, packLabel(sp.code)),
    ),
    el(
      'span.state',
      {},
      sp.audio.length
        ? el(
            'span.counts',
            {},
            String(sp.audio.length),
            // The glyph is the noun here, so it gets announced rather than hidden.
            icon('music', { size: 13, label: t('library.row.recordings') }),
          )
        : el('span.counts', {}),
      masteryDots(standing, now),
    ),
  );
  link.addEventListener('click', (e) => {
    e.preventDefault();
    ctx.navigate(`/species/${sp.code}`);
  });

  const aside = el('button.setaside.btn.btn-plain', {
    type: 'button',
  }) as HTMLButtonElement;
  const row = el('div.sprow', {}, link, aside);

  // One function paints the row's set-aside state, and it is called on build as
  // well as on every toggle — so "ignored" is described in exactly one place
  // whether the row arrives that way or gets there by being tapped.
  const paint = () => {
    const off = isIgnored(sp.code);
    row.classList.toggle('aside', off);
    aside.replaceChildren(icon(off ? 'undo' : 'set-aside', { size: 17 }));
    aside.setAttribute(
      'aria-label',
      off
        ? t('library.row.studyAgain', { name: sp.common })
        : t('library.row.setAside', { name: sp.common }),
    );
    aside.title = off ? t('library.row.studyAgainTitle') : t('library.row.setAsideTitle');
  };
  aside.addEventListener('click', () => {
    toggleIgnored(sp.code);
    paint();
    onSetAside();
  });
  paint();
  return row;
}

/**
 * Families in the manifest's own order, which is taxonomic — so the groups
 * read down the list the way a field guide does, not alphabetically.
 */
function groupByFamily(species: Species[]): Map<string, Species[]> {
  const groups = new Map<string, Species[]>();
  for (const sp of species) {
    const family = sp.family ?? t('library.family.unplaced');
    const bucket = groups.get(family);
    if (bucket) bucket.push(sp);
    else groups.set(family, [sp]);
  }
  return groups;
}

/** The recordings and photos a set of birds carries, for the tally's message. */
function media(of: Species[]): { recordings: number; photos: number } {
  return {
    recordings: of.reduce((n, sp) => n + sp.audio.length, 0),
    photos: of.reduce((n, sp) => n + sp.photos.length, 0),
  };
}
